import { DialogSaurus } from '../dialog-saurus/dialog-saurus';
import { useCallback, useRef, useState } from 'react';
import { DialogScannerPermissao } from './components/dialog-scanner-permissao';
import { useAuth } from '../../../../services/app/use-cases/auth';
import { useKdsToken, useToastSaurus } from '../../../../services/app';
import { DefaultFormRefs } from '../../form/utils';
import { LoginFormModel } from '../../../../model/app';
import { RefreshProps, usePostIdentidadeRefresh } from '../../../../data/api/gestao/identidade/post-refresh';
import { useGetPerfilById } from '../../../../data/api/gestao/perfil/get-perfil-by-id';
import { EnumTpPermissaoUsuario } from '../../../../model/enums/enum-tp-permissao-usuario';
import { EnumCodigosPermissoes } from '../../../../model/enums/enum-codigos-permissoes';
import { Box, Button, Grid, Typography } from '@material-ui/core';
import { FormLogin } from '../../form/master/login/form-login';
import { VoltarIcon } from '../../icons';
import { ConfirmarIcon } from '../../icons/confirmar-icon';
import { QrCodeIcon } from '../../icons/qr-code-icon';
import { PerfilPermissaoModel } from '../../../../model/api/gestao/perfil';
import { useDialog } from '../../../../services/app/providers/dialog-provider';
import { useThemeQueries } from '../../../theme';

export interface DialogSolicitarPermissaoProps {
  openned: boolean;
  cod: number;
  acao: (...args: any[]) => Promise<any>;
  tipoPermissao?: EnumTpPermissaoUsuario;
  valor?: any;
  label?: string;
  cancelAction?: () => void;
}

export const DialogSolicitarPermissao = ({
  openned,
  acao,
  cod,
  label,
  tipoPermissao = EnumTpPermissaoUsuario.SimNao,
  valor,
  cancelAction,
  ...props
}: DialogSolicitarPermissaoProps) => {
  const { auth, carregando: carregandoAuth } = useAuth();
  const { showToast } = useToastSaurus();
  const { convertToken, getPessoaUsuario } = useKdsToken();
  const { fecharSolicitarPermissao } = useDialog()
  const { theme } = useThemeQueries()


  const loginRefs = useRef<DefaultFormRefs<LoginFormModel>>(null);
  const { postIdentidadeRefresh, carregando: refreshToken } = usePostIdentidadeRefresh()
  const { getPerfilById, carregando: carregandoPerfilById } = useGetPerfilById();

  const carregando = carregandoPerfilById || refreshToken || carregandoAuth

  const [scanner, setScanner] = useState<boolean>(false)

  const validar = useCallback(async (token: string) => {
    try {
      const user = convertToken(token);
      const perfilIds = Array<string>();
      //PREENCHO OS PERFIS DELE (DISTINCT)
      if (user?.empresa) {
        for (let i = 0; i < user?.empresa.length; i++) {
          if (perfilIds.indexOf(user?.empresa[i]?.Perfil?.Id) === -1) {
            perfilIds.push(user?.empresa[i]?.Perfil?.Id);
          }
        }
      }

      let permitido = false

      //FACO A BUSCA NA API PELOS PERFIS DO USUARIO
      for (let i = 0; i < perfilIds.length; i++) {
        const retApiPerfil = await getPerfilById(perfilIds[i]);

        if (retApiPerfil.statusCode === 400) {
          throw new Error(`O usuário ${user?.email} não está cadastrado.`);
        }

        if (retApiPerfil.erro) {
          throw retApiPerfil.erro;
        }

        const permissoes = retApiPerfil.resultado?.data.permissoes as PerfilPermissaoModel[]

        permitido = permissoes.some(element => {
          if (element.codigo === EnumCodigosPermissoes.DESCONTO_VENDA || element.codigo === EnumCodigosPermissoes.DESCONTO_ITEM) {
            return element.codigo === cod && Number(valor) <= Number(element.valor)
          }
          return element.codigo === cod && element.valor === 'true'
        })
        if (permitido) break
      }

      if (permitido) {
        await acao(token, user) //caso precise do token para fazer requisição passo ele aqui, basta usar no callback
        fecharSolicitarPermissao();
        return
      }

      throw new Error('Usuário inserido não possui permissão.')
    } catch (e: any) {
      showToast('error', e.message)
    }
  }, [acao, cod, convertToken, fecharSolicitarPermissao, getPerfilById, showToast, valor])

  const handleSubmit = useCallback(async (model: LoginFormModel) => {
    try {
      const res = await auth({ usuario: model.usuario, senha: model.senha, contratoId: getPessoaUsuario().pessoa.contratoId ?? '' })

      if (res.statusCode === 403) throw new Error('o usuário informado não é válido.')
      if (res.statusCode === 400) throw new Error('Usuário não identificado.')
      if (res.erro) throw res.erro

      const token = res.resultado?.data.accessToken


      validar(token);
    } catch (e: any) {
      showToast('error', e.message)
    }
  }, [auth, getPessoaUsuario, showToast, validar])


  const onScan = useCallback(async (jsonObj: string) => {
    try {
      const data = JSON.parse(jsonObj) as RefreshProps
      const res = await postIdentidadeRefresh(data)

      validar(res.resultado?.data.accessToken)
    } catch (e: any) {
      showToast('error', e.message)
    }
  }, [postIdentidadeRefresh, showToast, validar])

  return (
    <DialogSaurus
      aberto={openned || false}
      titulo="Solicitar Permissão"
      tamanho="sm"
    >
      <Grid container spacing={1} style={{ marginBottom: 8 }}>
        <Grid item xs={12}>
          <Typography>
            Você não possui permissão para {
              label
                ? <Box component="span" fontWeight="bold" color={theme.palette.primary.main}>{label}</Box>
                : 'realizar esta ação'
            }.
            Autentique com um usuário com permissão para concluir esta ação.
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <FormLogin
            ref={loginRefs}
            loading={carregando}
            onSubmit={handleSubmit}
            showLoading={carregando}
          />
        </Grid>
        <Grid item xs={6} md={6}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => {
              cancelAction?.();
              fecharSolicitarPermissao();
            }}
            fullWidth
            className="round"
          >
            <VoltarIcon tipo="BUTTON" />
            Cancelar
          </Button>
        </Grid>
        <Grid item xs={6} md={6}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => loginRefs.current?.submitForm()}
            fullWidth
            className="round"
          >
            <ConfirmarIcon tipo="BUTTON_PRIMARY" />
            Confirmar
          </Button>
        </Grid>
        <Grid item xs={12}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setScanner((prev) => !prev)}
            fullWidth
            className="round"
          >
            <QrCodeIcon tipo="BUTTON" />
            Escanear QRCode
          </Button>
        </Grid>
      </Grid>
      {scanner && (
        <DialogScannerPermissao
          closeModal={() => setScanner(false)}
          handleScan={onScan}
          openned={scanner}
        />
      )}
    </DialogSaurus>
  );
};